<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'next/button/Button.vue';
import ConfirmButton from 'next/button/ConfirmButton.vue';
import Input from 'next/input/Input.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('wgptMcpServers/getUIFlags');

const isKeyMasked = ref(true);

const endpointUrl = computed(() => props.server.endpoint_url || '');
const accessToken = computed(() => props.server.access_token || '');
const clientKey = computed(
  () =>
    (props.server.name || 'whatsgpt')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'whatsgpt'
);

const jsonSnippet = computed(() =>
  JSON.stringify(
    {
      mcpServers: {
        [clientKey.value]: {
          url: endpointUrl.value,
          headers: { Authorization: `Bearer ${accessToken.value}` },
        },
      },
    },
    null,
    2
  )
);

const curlSnippet = computed(() =>
  [
    `curl -X POST '${endpointUrl.value}' \\`,
    `  -H 'Authorization: Bearer ${accessToken.value}' \\`,
    "  -H 'Content-Type: application/json' \\",
    "  -H 'Accept: application/json, text/event-stream' \\",
    "  -H 'MCP-Protocol-Version: 2026-07-28' -H 'Mcp-Method: tools/list' \\",
    `  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{"_meta":{"io.modelcontextprotocol/protocolVersion":"2026-07-28","io.modelcontextprotocol/clientCapabilities":{}}}}'`,
  ].join('\n')
);

const snippets = computed(() => [
  {
    key: 'json',
    label: t('WGPT_MCP_SERVERS.EDIT.CONNECTION.SNIPPETS.JSON'),
    code: jsonSnippet.value,
  },
  {
    key: 'curl',
    label: t('WGPT_MCP_SERVERS.EDIT.CONNECTION.SNIPPETS.CURL'),
    code: curlSnippet.value,
  },
]);

const copyToClipboard = async (text, successMessage) => {
  await navigator.clipboard.writeText(text);
  useAlert(successMessage);
};

const regenerateToken = async () => {
  try {
    await store.dispatch('wgptMcpServers/regenerateToken', props.server.id);
    useAlert(
      t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.API.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="mx-6 min-h-full flex flex-col gap-8">
    <div class="pt-4">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.DESCRIPTION') }}
      </p>
      <p class="text-xs text-n-slate-10 mt-2">
        {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.PROTOCOL_NOTE') }}
      </p>
    </div>

    <div class="flex flex-col gap-2 max-w-3xl">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ENDPOINT.LABEL') }}
      </span>
      <div class="flex items-center gap-2">
        <Input :model-value="endpointUrl" readonly class="flex-1" />
        <Button
          :label="t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.COPY')"
          icon="i-lucide-copy"
          slate
          outline
          @click="
            copyToClipboard(
              endpointUrl,
              t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ENDPOINT.COPY_SUCCESSFUL')
            )
          "
        />
      </div>
    </div>

    <div class="flex flex-col gap-2 max-w-3xl">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.LABEL') }}
      </span>
      <div class="flex items-center gap-2">
        <Input
          :model-value="accessToken"
          :type="isKeyMasked ? 'password' : 'text'"
          readonly
          class="flex-1"
        />
        <Button
          :icon="isKeyMasked ? 'i-lucide-eye' : 'i-lucide-eye-off'"
          slate
          outline
          @click="isKeyMasked = !isKeyMasked"
        />
        <Button
          :label="t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.COPY')"
          icon="i-lucide-copy"
          slate
          outline
          @click="
            copyToClipboard(
              accessToken,
              t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.COPY_SUCCESSFUL')
            )
          "
        />
        <ConfirmButton
          :label="t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.RESET')"
          :confirm-label="
            t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.CONFIRM_RESET')
          "
          :confirm-hint="
            t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.CONFIRM_HINT')
          "
          color="slate"
          confirm-color="ruby"
          variant="outline"
          icon="i-lucide-key-round"
          :disabled="uiFlags.isRegenerating"
          @click="regenerateToken"
        />
      </div>
      <p class="text-xs text-n-slate-11 max-w-md">
        {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.ACCESS_TOKEN.HELP') }}
      </p>
    </div>

    <div class="flex flex-col gap-4 max-w-3xl pb-8">
      <div>
        <h4 class="text-base font-medium text-n-slate-12">
          {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.SNIPPETS.TITLE') }}
        </h4>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('WGPT_MCP_SERVERS.EDIT.CONNECTION.SNIPPETS.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-for="snippet in snippets"
        :key="snippet.key"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ snippet.label }}
          </span>
          <Button
            :label="t('WGPT_MCP_SERVERS.EDIT.CONNECTION.SNIPPETS.COPY')"
            icon="i-lucide-copy"
            size="xs"
            slate
            ghost
            @click="
              copyToClipboard(
                snippet.code,
                t('WGPT_MCP_SERVERS.EDIT.CONNECTION.SNIPPETS.COPY_SUCCESSFUL')
              )
            "
          />
        </div>
        <pre
          class="text-xs text-n-slate-12 bg-n-alpha-2 rounded-lg p-3 overflow-auto whitespace-pre-wrap break-all"
          >{{ snippet.code }}</pre
        >
      </div>
    </div>
  </div>
</template>
